import { fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import Component from '@glimmer/component';
import {
  PrismButton,
  PrismButtonGroup,
  PrismCard,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';
import GeneralDropdownsDropdownYield from 'lumen/components/general/dropdowns/dropdown-yield';
import GeneralDropdownsTriggersTriggerDropdownIconButton from 'lumen/components/general/dropdowns/triggers/trigger-dropdown-icon-button';
import {
  GROUP_COLUMNS_BY,
  type GroupColumnsBy,
  isGroupColumnsBy,
  isSortOn,
  SORT_ON,
  type SortOn,
  type TableLayoutPreferences,
} from 'lumen/utils/constants/market-snapshots-constants';
import { localCopy } from 'tracked-toolbox';

export interface MarketSnapshotsTableLayoutSignature {
  Args: {
    onSave: (preferences: TableLayoutPreferences) => void;
    tableLayoutPreferences: TableLayoutPreferences;
  };
}

export default class MarketSnapshotsTableLayout extends Component<MarketSnapshotsTableLayoutSignature> {
  @localCopy('args.tableLayoutPreferences.groupColumnsBy')
  declare groupColumnsBy: GroupColumnsBy;

  @localCopy('args.tableLayoutPreferences.sortOn')
  declare sortOn: SortOn;

  @action
  onCancel(dropdown?: { toggleDropdown: () => void }): void {
    this.groupColumnsBy = this.args.tableLayoutPreferences.groupColumnsBy;
    this.sortOn = this.args.tableLayoutPreferences.sortOn;
    dropdown?.toggleDropdown();
  }

  @action
  onGroupColumnsByChange(groupColumnsBy: string | string[]): void {
    if (isGroupColumnsBy(groupColumnsBy)) {
      this.groupColumnsBy = groupColumnsBy;
    }
  }

  @action
  onSave(dropdown?: { toggleDropdown: () => void }): void {
    this.args.onSave({
      groupColumnsBy: this.groupColumnsBy,
      sortOn: this.sortOn,
    });
    dropdown?.toggleDropdown();
  }

  @action
  onSortOnChange(sortOn: string | string[]): void {
    if (isSortOn(sortOn)) {
      this.sortOn = sortOn;
    }
  }

  <template>
    <div class="relative">
      {{#let
        (component
          GeneralDropdownsTriggersTriggerDropdownIconButton
          buttonType="secondary"
          dataTestId="table-layout-trigger"
          iconBefore="settings"
        )
        as |triggerComponent|
      }}
        <GeneralDropdownsDropdownYield
          @dropdownClass=".dropdown-target"
          {{! @glint-expect-error: Incorrect type }}
          @dropdownClassName="dropdown-target w-[320px] top-[40px] right-0"
          @label={{t "market.snapshots.table_layout"}}
          @onCancel={{this.onCancel}}
          @selected={{null}}
          @triggerComponent={{triggerComponent}}
          as |dropdown|
        >
          <PrismCard>
            <:header as |Header|>
              <Header.title>
                {{t "market.snapshots.table_layout"}}
              </Header.title>
            </:header>
            <:content>
              <div class="grid gap-400">
                <div class="flex items-center justify-between">
                  <span class="text-200--medium">
                    {{t "market.snapshots.sort_on"}}
                  </span>

                  <PrismButtonGroup
                    @onChange={{this.onSortOnChange}}
                    @value={{this.sortOn}}
                    aria-label={{t "market.snapshots.sort_on"}}
                    as |ButtonGroup|
                  >
                    <ButtonGroup.button
                      @label={{t "market.snapshots.sort_on.actuals"}}
                      @value={{SORT_ON.ACTUALS}}
                      data-test-id="sort-on-actuals"
                    />
                    <ButtonGroup.button
                      @label={{t "market.snapshots.sort_on.variance"}}
                      @value={{SORT_ON.VARIANCE}}
                      data-test-id="sort-on-variance"
                    />
                  </PrismButtonGroup>
                </div>

                <div class="flex items-center justify-between">
                  <span class="text-200--medium">
                    {{t "market.snapshots.group_columns_by"}}
                  </span>

                  <PrismButtonGroup
                    @onChange={{this.onGroupColumnsByChange}}
                    @value={{this.groupColumnsBy}}
                    aria-label={{t "market.snapshots.group_columns_by"}}
                    as |ButtonGroup|
                  >
                    <ButtonGroup.button
                      @label={{t "market.snapshots.group_columns.categories"}}
                      @value={{GROUP_COLUMNS_BY.CATEGORIES}}
                    />
                    <ButtonGroup.button
                      @label={{t "market.snapshots.group_columns.kpis"}}
                      @value={{GROUP_COLUMNS_BY.KPIS}}
                    />
                  </PrismButtonGroup>
                </div>
              </div>
            </:content>
            <:footer>
              <div class="flex flex-row justify-end gap-200">
                <PrismButton
                  @buttonType="secondary"
                  @label={{t "general.cancel"}}
                  {{on "click" (fn this.onCancel dropdown)}}
                />
                <PrismButton
                  @label={{t "general.apply"}}
                  {{on "click" (fn this.onSave dropdown)}}
                />
              </div>
            </:footer>
          </PrismCard>
        </GeneralDropdownsDropdownYield>
      {{/let}}
    </div>
  </template>
}
